import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import Breadcrumbs from "../BreadCrum/BreadCrum";
import PageHeadingSection from "../pageheading/PageHeadingSection";
import CustomSelect from "../formfields/CustomSelect";
import staffService from "../../../services/staffService";
import { emergencyApi, readSession, apiMessage } from "./emergencyApi";

const EmergencyTypeForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const { user } = useSelector((state) => state.auth);
    const { siteId, userId } = readSession(user);
    const existing = location.state || {};
    const savedRecipientIds = (existing.recipients || []).map((item) => Number(item)).filter((item) => item > 0);
    const [name, setName] = useState(existing.name || "");
    const [subTypes, setSubTypes] = useState((existing.sub_types || []).map((item) => item.name).join(", "));
    const [roles, setRoles] = useState([]);
    const [role, setRole] = useState(null);
    const [staffOptions, setStaffOptions] = useState([]);
    const [staff, setStaff] = useState(savedRecipientIds.map((item) => ({ value: item, label: `Staff ${item}` })));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        staffService.getStaffRoles(siteId).then((response) => {
            const list = response.data?.data || [];
            setRoles(list.map((item) => ({
                value: item.staffRoleId || item.id,
                label: item.roleName || item.name || "Role"
            })));
        }).catch(() => toast.error("Failed to load staff roles"));
    }, [siteId]);

    useEffect(() => {
        if (!id || !siteId) return;
        staffService.getStaff({ siteId, pageNumber: 1, pageSize: 500 }).then((response) => {
            const list = response.data?.data || [];
            const options = list.map((item) => ({
                value: item.staffId || item.id,
                label: `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.fullName || `Staff ${item.staffId}`
            }));
            const selected = options.filter((item) => savedRecipientIds.includes(Number(item.value)));
            const missing = savedRecipientIds
                .filter((item) => !selected.some((option) => Number(option.value) === item))
                .map((item) => ({ value: item, label: `Staff ${item}` }));
            setStaff([...selected, ...missing]);
        }).catch(() => toast.error("Failed to load assigned staff"));
    }, [id, siteId]);

    useEffect(() => {
        if (!role?.value) {
            setStaffOptions([]);
            return;
        }
        staffService.getStaff({ staffRoleId: role.value, siteId, pageNumber: 1, pageSize: 200 }).then((response) => {
            const list = response.data?.data || [];
            setStaffOptions(list.map((item) => ({
                value: item.staffId || item.id,
                label: `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.fullName || `Staff ${item.staffId}`
            })));
        }).catch(() => toast.error("Failed to load staff"));
    }, [role, siteId]);

    const save = async () => {
        if (!name.trim()) {
            toast.error("Enter a type name");
            return;
        }
        if (userId <= 0) {
            toast.error("userId is missing from the login session");
            return;
        }
        setSaving(true);
        const recipientIds = staff.map((item) => Number(item.value)).filter((item) => item > 0);
        try {
            const response = id
                ? await emergencyApi.updateType(id, {
                    userId,
                    name: name.trim(),
                    site_id: String(siteId),
                    recipients: recipientIds
                })
                : await emergencyApi.createType({
                    userId,
                    name: name.trim(),
                    site_id: String(siteId),
                    recipients: recipientIds,
                    sub_types: subTypes.split(",").map((item) => item.trim()).filter(Boolean).map((item) => ({ name: item }))
                });
            if (response.data?.isSuccess === false || response.data?.IsSuccess === false) {
                toast.error(response.data?.message || response.data?.Message || "Failed to save type");
                return;
            }
            toast.success(id ? "Emergency type updated" : "Emergency type created");
            navigate("/common/emergency-services/types");
        } catch (error) {
            toast.error(apiMessage(error, "Failed to save type"));
        } finally {
            setSaving(false);
        }
    };

    const staffChoices = (() => {
        const byId = new Map();
        staff.forEach((item) => byId.set(Number(item.value), item));
        staffOptions.forEach((item) => byId.set(Number(item.value), item));
        return Array.from(byId.values());
    })();

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs items={["Emergency Services", "Types", id ? "Edit" : "Add"]} />
            <PageHeadingSection
                mainHeading={id ? "Edit emergency type" : "Add emergency type"}
                subHeading="Choose a staff role, then the people who should be notified."
                primaryBtnText={saving ? "Saving" : "Save"}
                primaryBtnIcon={faSave}
                primaryBtnDisabled={saving}
                PrimaryOnClick={save}
            />
            <div className="mt-4 grid gap-4 max-w-xl">
                <label className="text-xs font-semibold text-textprimary">
                    Name
                    <input className="mt-1 w-full border border-bordercolor rounded-md px-3 py-2 text-sm" value={name} onChange={(event) => setName(event.target.value)} />
                </label>
                {!id && (
                    <label className="text-xs font-semibold text-textprimary">
                        Sub types
                        <input className="mt-1 w-full border border-bordercolor rounded-md px-3 py-2 text-sm" value={subTypes} placeholder="Comma separated" onChange={(event) => setSubTypes(event.target.value)} />
                    </label>
                )}
                <CustomSelect label="Staff role" options={roles} value={role} onChange={setRole} placeholder="Select a role" />
                <CustomSelect label="Staff" options={staffChoices} value={staff} onChange={setStaff} isMulti placeholder="Select staff" />
            </div>
        </div>
    );
};

export default EmergencyTypeForm;
